import { useEffect, useState } from "react";
import { AuthApi } from "../lib/api";
import { Shell } from "./Shell";
import { Button } from "./primitives";
import { useToast } from "../lib/toast";

type Phase = "checking" | "onboard" | "login" | "ready" | "error";

export function AuthGate() {
  const [phase, setPhase] = useState<Phase>("checking");
  const [publicUrl, setPublicUrl] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => { void checkState(); }, []);

  async function checkState() {
    try {
      const s = await AuthApi.state();
      setPublicUrl(s.public_url);
      if (!s.onboarded) setPhase("onboard");
      else if (!s.authenticated) setPhase("login");
      else setPhase("ready");
    } catch (err) {
      setErrorMsg(String(err));
      setPhase("error");
    }
  }

  if (phase === "checking") {
    return (
      <div className="h-screen flex items-center justify-center text-fg-muted text-sm">
        Loading…
      </div>
    );
  }
  if (phase === "error") {
    return (
      <div className="h-screen flex items-center justify-center text-danger text-sm">
        {errorMsg}
      </div>
    );
  }
  if (phase === "onboard") return <OnboardingPanel publicUrlDefault={publicUrl} onDone={() => setPhase("ready")} />;
  if (phase === "login")   return <LoginPanel publicUrl={publicUrl} onSuccess={() => setPhase("ready")} />;
  return <Shell />;
}

// ── Onboarding ────────────────────────────────────────────────────────────

function OnboardingPanel({ publicUrlDefault, onDone }: { publicUrlDefault: string; onDone: () => void }) {
  const toast = useToast();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [publicUrl, setPublicUrl] = useState(publicUrlDefault);
  const [saving, setSaving] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password too short", "Use at least 6 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    try { new URL(publicUrl); } catch { return toast.error("Public URL must be a valid URL"); }
    setSaving(true);
    try {
      await AuthApi.onboard({ password, public_url: publicUrl });
      onDone();
    } catch (err) {
      toast.error("Onboarding failed", String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <CenteredCard title="Welcome to the agent fleet" subtitle="One-time setup">
      <form onSubmit={submit} className="space-y-3 text-sm">
        <p className="text-xs text-fg-muted">
          This UI lives on a public domain. Pick the URL your reverse proxy points at, and set an admin
          password — you'll need it on every device that opens this panel.
        </p>
        <label className="block">
          <span className="block text-xs text-fg-muted mb-1">Public URL</span>
          <input
            value={publicUrl}
            onChange={(e) => setPublicUrl(e.target.value)}
            className="w-full text-sm font-mono"
            placeholder="https://agents.nge.pet"
          />
          <span className="block text-[11px] text-fg-subtle mt-0.5">
            Used for the webhook URLs surfaced in the Channels page. Format: <code>https://host</code> (no trailing slash).
          </span>
        </label>
        <label className="block">
          <span className="block text-xs text-fg-muted mb-1">Admin password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full text-sm"
            autoComplete="new-password"
            minLength={6}
          />
        </label>
        <label className="block">
          <span className="block text-xs text-fg-muted mb-1">Confirm password</span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full text-sm"
            autoComplete="new-password"
            minLength={6}
          />
        </label>
        <Button type="submit" loading={saving} disabled={!password || !confirm}>
          Complete setup
        </Button>
      </form>
    </CenteredCard>
  );
}

// ── Login ─────────────────────────────────────────────────────────────────

function LoginPanel({ publicUrl, onSuccess }: { publicUrl: string; onSuccess: () => void }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryAfterMs, setRetryAfterMs] = useState<number>(0);

  // Countdown for retry_after_ms when the server reports a lockout.
  useEffect(() => {
    if (retryAfterMs <= 0) return;
    const id = setInterval(() => setRetryAfterMs((ms) => Math.max(0, ms - 1000)), 1000);
    return () => clearInterval(id);
  }, [retryAfterMs > 0]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!password) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await AuthApi.login(password);
      if (res.status === 200 && res.ok) {
        setRetryAfterMs(0);
        onSuccess();
      } else {
        setError(res.error ?? "Login failed");
        if (res.retry_after_ms) setRetryAfterMs(res.retry_after_ms);
      }
    } catch (err) {
      setError(String(err));
    } finally {
      setSubmitting(false);
    }
  }

  const locked = retryAfterMs > 0;

  return (
    <CenteredCard title="Sign in" subtitle={publicUrl}>
      <form onSubmit={submit} className="space-y-3 text-sm">
        <label className="block">
          <span className="block text-xs text-fg-muted mb-1">Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full text-sm"
            autoComplete="current-password"
            disabled={locked}
            autoFocus
          />
        </label>
        {error && (
          <p className="text-xs text-danger">
            {error}
            {locked && <span className="block mt-0.5">Try again in {formatDuration(retryAfterMs)}.</span>}
          </p>
        )}
        <Button type="submit" loading={submitting} disabled={!password || locked}>
          Sign in
        </Button>
      </form>
    </CenteredCard>
  );
}

function formatDuration(ms: number): string {
  if (ms < 60_000) return `${Math.ceil(ms / 1000)}s`;
  if (ms < 3600_000) return `${Math.ceil(ms / 60_000)}m`;
  return `${(ms / 3600_000).toFixed(1)}h`;
}

// ── Shared centered-card layout ──────────────────────────────────────────

function CenteredCard({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="h-screen flex items-center justify-center bg-app">
      <div className="w-[28rem] panel-elevated p-6 space-y-4 animate-fade-in">
        <div>
          <h1 className="text-lg font-semibold text-fg">{title}</h1>
          {subtitle && <p className="text-xs text-fg-subtle mt-0.5 font-mono break-all">{subtitle}</p>}
        </div>
        {children}
      </div>
    </div>
  );
}
